import React, { useState, useEffect } from 'react';
import {
  RocketLaunchIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  UserGroupIcon,
  TableCellsIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

export const DocumentationPage: React.FC = () => {
  const [activeFaq, setActiveFaq] = useState<string | null>(null);

  const faqs = [
    {
      id: 'first-campaign',
      question: 'How do I create my first SMS campaign?',
      answer:
        'Go to the SMS Campaign page, choose a Sender ID, select your audience (contacts, imports or groups), write your message, preview the summary and then send or schedule the campaign.'
    },
    {
      id: 'quick-vs-campaign',
      question: 'What is the difference between Quick SMS and SMS Campaign?',
      answer:
        'Quick SMS is for one-off messages to a small set of numbers, while SMS Campaigns are built for larger, structured sends with saved audiences, templates and detailed delivery reporting.'
    },
    {
      id: 'import-excel',
      question: 'How can I import contacts from an Excel file?',
      answer:
        'Prepare an Excel or CSV file with phone numbers in 263 format, then use the Import Contacts or group import tools to upload, validate and save them for use in campaigns and Quick SMS.'
    },
    {
      id: 'sender-id',
      question: 'How do I request or change a Sender ID?',
      answer:
        'Open the Sender ID or Settings area, follow the on-screen steps to apply for a new Sender ID, then select it from the Sender ID dropdown when sending SMS once it is approved.'
    },
    {
      id: 'phone-format',
      question: 'What phone number format should I use?',
      answer:
        'Use full Zimbabwe numbers starting with 263 and exactly 12 digits (for example 263712345678); the platform rejects numbers that do not match this format.'
    },
    {
      id: 'groups-vs-contacts',
      question: 'What is the difference between contacts and groups?',
      answer:
        'Contacts store individual people and their details, while groups let you bundle phone numbers under a shared name so you can target them together in SMS Campaigns.'
    },
    {
      id: 'templates-use',
      question: 'How do templates work when sending SMS?',
      answer:
        'On Quick SMS or SMS Campaign, pick a template from the dropdown to prefill the message body, then adjust it as needed before previewing and sending.'
    },
    {
      id: 'credits-check',
      question: 'How do I check my available SMS credits?',
      answer:
        'Your remaining credits are shown on the dashboard and in relevant billing widgets, and they update automatically as messages are queued and sent.'
    },
    {
      id: 'logs-usage',
      question: 'How can I see if my messages were delivered?',
      answer:
        'Use the SMS Logs page to filter by status, phone or date, then review delivered, pending and failed messages or export logs for deeper analysis.'
    },
    {
      id: 'account-updates',
      question: 'Where can I change my email, phone or password?',
      answer:
        'Go to Account Settings to update your profile information and use the Change Password section to safely update your login password.'
    },
    {
      id: 'notifications-control',
      question: 'How do I control which notifications I receive?',
      answer:
        'Open the Notifications settings page, adjust the toggles for each notification type and choose a frequency such as Instant, Daily, Weekly or Never.'
    }
  ];

  useEffect(() => {
    const handleClickOutside = () => {
      if (activeFaq !== null) {
        setActiveFaq(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeFaq]);

  const documentationSections = [
    {
      title: 'Quick Start Guide',
      icon: <RocketLaunchIcon className="w-8 h-8 text-[#FF5722]" />,
      description: 'Get started with the platform basics',
      link: '/docs/quick-start'
    },
    {
      title: 'How to create SMS campaign',
      icon: <EnvelopeIcon className="w-8 h-8 text-[#FF5722]" />,
      description: 'Learn to create and manage SMS campaigns',
      link: '/docs/sms-campaign'
    },
    {
      title: 'How to send Quick SMS',
      icon: <DocumentTextIcon className="w-8 h-8 text-[#FF5722]" />,
      description: 'Send individual SMS messages quickly',
      link: '/docs/quick-sms'
    },
    {
      title: 'Contact Management',
      icon: <UserGroupIcon className="w-8 h-8 text-[#FF5722]" />,
      description: 'Manage your contacts and groups',
      link: '/docs/contacts'
    },
    {
      title: 'Template Management',
      icon: <TableCellsIcon className="w-8 h-8 text-[#FF5722]" />,
      description: 'Manage your templates',
      link: '/docs/bulk-sms'
    },
    {
      title: 'SMS Logs Review',
      icon: <UserGroupIcon className="w-8 h-8 text-[#FF5722]" />,
      description: 'Review your SMS logs over time',
      link: '/docs/sms-logs'
    },
    {
      title: 'Settings',
      icon: <ChatBubbleLeftRightIcon className="w-8 h-8 text-[#FF5722]" />,
      description: 'Manage your Account and Notifications',
      link: '/docs/quick-import'
    }
  ];

  return (
    <div
      className="min-h-screen bg-gray-50"
    >
      {/* Header Section */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Documentation & Help Center</h1>
          <p className="mt-2 text-sm text-gray-600">
            Find comprehensive guides, tutorials, and answers to frequently asked questions
          </p>
        </div>
      </div>

      {/* Banner Section */}
      <div className="w-full bg-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="relative h-[200px] rounded-lg overflow-hidden bg-gray-900 flex items-center justify-center">
            <img
              src="/images/documentation-banner.jpg"
              alt="Documentation Banner"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      </div>

      {/* Introduction Text */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Getting Started with Our Platform
          </h3>
          <p className="text-gray-600">
            Our documentation provides comprehensive guides and tutorials to help you make the most
            of our SMS Management Platform. Whether you're new to the platform or looking to explore
            advanced features, you'll find everything you need here.
          </p>
        </div>
      </div>

      {/* Documentation Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documentationSections.map((section, index) => (
            <Link
              key={index}
              to={section.link}
              className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {section.icon}
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {section.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {section.description}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-6">
            <QuestionMarkCircleIcon className="w-6 h-6 text-[#FF5722]" />
            <h3 className="text-xl font-semibold text-gray-900">
              Frequently Asked Questions
            </h3>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="w-full text-left p-4 flex items-center justify-between"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveFaq((current) => (current === faq.id ? null : faq.id));
                  }}
                >
                  <h4 className="text-lg font-medium text-gray-900">
                    {faq.question}
                  </h4>
                  <span className="ml-4 text-gray-400">
                    {activeFaq === faq.id ? '−' : '+'}
                  </span>
                </button>
                {activeFaq === faq.id && (
                  <div className="px-4 pb-4 text-sm text-gray-700 border-t border-gray-100">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage; 